import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Save, Upload, Download, Database, RefreshCw, FileText,
  Building2, DollarSign, Leaf, Droplets, Users, Shield, Truck, FileCheck
} from 'lucide-react';
import { useAlert } from '../../context/AlertContext';

const API = process.env.REACT_APP_BACKEND_URL;

const ReportMetadata = ({ study }) => {
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const [metadata, setMetadata] = useState({});
  const [activeTab, setActiveTab] = useState('company');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    if (study?.id) {
      fetchMetadata();
    }
  }, [study?.id]);

  const fetchMetadata = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/api/metadata/${study.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMetadata(response.data.metadata || {});
    } catch (error) {
      console.error('Error fetching metadata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setMetadata(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      const response = await axios.post(
        `${API}/api/metadata/${study.id}`,
        metadata,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess(`Metadata saved! ${response.data.completion_percent}% complete (${response.data.filled_fields}/${response.data.total_fields} fields)`);
    } catch (error) {
      console.error('Error saving metadata:', error);
      showError('Failed to save metadata');
    } finally {
      setSaving(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setUploadStatus(null);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      showWarning('Please select a file first');
      return;
    }

    setUploadStatus({ status: 'uploading', message: 'Uploading...' });
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await axios.post(
        `${API}/api/metadata/${study.id}/upload-excel`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      setUploadStatus({
        status: 'success',
        message: `Success! Processed ${response.data.sheets_processed.length} sheets, extracted ${response.data.fields_extracted} fields`,
        data: response.data
      });
      setMetadata(response.data.metadata);
      setSelectedFile(null);
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadStatus({
        status: 'error',
        message: error.response?.data?.detail || 'Upload failed'
      });
    }
  };

  const calculateCompletion = () => {
    const totalFields = 60; // Total number of fields
    const filledFields = Object.values(metadata).filter(v => v && v.trim() !== '').length;
    return {
      filled: filledFields,
      total: totalFields,
      percent: Math.round((filledFields / totalFields) * 100)
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-3 text-lg">Loading metadata...</span>
      </div>
    );
  }

  const completion = calculateCompletion();

  const tabs = [
    { id: 'company', label: 'Company Info', icon: Building2 },
    { id: 'financial', label: 'Financial', icon: DollarSign },
    { id: 'environmental', label: 'Environmental', icon: Leaf },
    { id: 'water_waste', label: 'Water & Waste', icon: Droplets },
    { id: 'social', label: 'Social & Employee', icon: Users },
    { id: 'governance', label: 'Governance', icon: Shield },
    { id: 'supply_chain', label: 'Supply Chain', icon: Truck },
    { id: 'narratives', label: 'Narratives', icon: FileText },
    { id: 'assurance', label: 'Assurance', icon: FileCheck }
  ];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-600 to-red-700 rounded-lg shadow-lg p-6 mb-6 text-white">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
              <Database className="w-8 h-8" />
              Report Metadata Management
            </h1>
            <p className="text-orange-100">
              Manage company information and ESG metrics for {study?.study_name}
            </p>

            {/* Progress Bar */}
            <div className="mt-4">
              <div className="flex justify-between text-sm text-white mb-2">
                <span>Completion: {completion.filled}/{completion.total} fields</span>
                <span className="font-semibold">{completion.percent}%</span>
              </div>
              <div className="w-full bg-white/30 rounded-full h-3">
                <div
                  className="bg-white h-3 rounded-full transition-all"
                  style={{ width: `${completion.percent}%` }}
                />
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Metadata
            </button>
          </div>
        </div>
      </div>

      {/* Excel Upload Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Excel Template Upload</h2>
        <div className="flex items-center gap-4">
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileSelect}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
          />
          <button
            onClick={handleFileUpload}
            disabled={!selectedFile}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            <Upload className="w-4 h-4" />
            Upload & Extract Data
          </button>
          <a
            href={`${API}/api/metadata/template/download`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            <Download className="w-4 h-4" />
            Download Template
          </a>
        </div>

        {uploadStatus && (
          <div className={`mt-4 p-4 rounded-lg ${
            uploadStatus.status === 'success' ? 'bg-green-50 text-green-800' :
            uploadStatus.status === 'error' ? 'bg-red-50 text-red-800' :
            'bg-blue-50 text-blue-800'
          }`}>
            <p className="font-semibold">{uploadStatus.message}</p>
            {uploadStatus.data && (
              <p className="text-sm mt-2">
                Sheets: {uploadStatus.data.sheets_processed.join(', ')}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="border-b border-gray-200 px-6">
          <div className="flex gap-2 overflow-x-auto">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 font-medium border-b-2 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-orange-600 text-orange-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-6">
          {/* Company Information Tab */}
          {activeTab === 'company' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Company Information</h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Corporate Identity Number (CIN) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={metadata.cin || ''}
                    onChange={(e) => handleInputChange('cin', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="L12345MH2010PLC123456"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={metadata.company_name || ''}
                    onChange={(e) => handleInputChange('company_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="ABC Limited"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Registered Office Address
                  </label>
                  <textarea
                    value={metadata.registered_office_address || ''}
                    onChange={(e) => handleInputChange('registered_office_address', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    rows={3}
                    placeholder="Full registered address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Corporate Website
                  </label>
                  <input
                    type="url"
                    value={metadata.corporate_website || ''}
                    onChange={(e) => handleInputChange('corporate_website', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="https://www.example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Email
                  </label>
                  <input
                    type="email"
                    value={metadata.contact_email || ''}
                    onChange={(e) => handleInputChange('contact_email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="contact@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Industry Sector
                  </label>
                  <input
                    type="text"
                    value={metadata.industry || ''}
                    onChange={(e) => handleInputChange('industry', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Manufacturing"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Financial Tab */}
          {activeTab === 'financial' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Financial Information</h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Financial Year <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={metadata.financial_year || ''}
                    onChange={(e) => handleInputChange('financial_year', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="FY2024-25"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Total Revenue (INR)
                  </label>
                  <input
                    type="text"
                    value={metadata.total_revenue || ''}
                    onChange={(e) => handleInputChange('total_revenue', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="1,000,000,000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    CSR Spend (INR)
                  </label>
                  <input
                    type="text"
                    value={metadata.csr_spend || ''}
                    onChange={(e) => handleInputChange('csr_spend', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="20,000,000"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Environmental Tab */}
          {activeTab === 'environmental' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Environmental Metrics</h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Scope 1 Emissions (tCO2e)
                  </label>
                  <input
                    type="text"
                    value={metadata.scope1_emissions || ''}
                    onChange={(e) => handleInputChange('scope1_emissions', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="1,234"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Scope 2 Emissions (tCO2e)
                  </label>
                  <input
                    type="text"
                    value={metadata.scope2_emissions || ''}
                    onChange={(e) => handleInputChange('scope2_emissions', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="5,678"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Scope 3 Categories
                  </label>
                  <input
                    type="text"
                    value={metadata.scope3_categories || ''}
                    onChange={(e) => handleInputChange('scope3_categories', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="9,012"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Emissions Intensity
                  </label>
                  <input
                    type="text"
                    value={metadata.emissions_intensity || ''}
                    onChange={(e) => handleInputChange('emissions_intensity', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="0.5 tCO2e/unit"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Electricity Consumption (kWh)
                  </label>
                  <input
                    type="text"
                    value={metadata.electricity_consumption || ''}
                    onChange={(e) => handleInputChange('electricity_consumption', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="1,000,000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Diesel Consumption (L)
                  </label>
                  <input
                    type="text"
                    value={metadata.fuel_consumption_diesel || ''}
                    onChange={(e) => handleInputChange('fuel_consumption_diesel', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="50,000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Renewable Capacity (MW)
                  </label>
                  <input
                    type="text"
                    value={metadata.renewable_capacity || ''}
                    onChange={(e) => handleInputChange('renewable_capacity', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="5"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Renewable Energy Share (%)
                  </label>
                  <input
                    type="text"
                    value={metadata.renewable_energy_share || ''}
                    onChange={(e) => handleInputChange('renewable_energy_share', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="25"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Water & Waste Tab */}
          {activeTab === 'water_waste' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Water & Waste Management</h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Water Withdrawal (KL)
                  </label>
                  <input
                    type="text"
                    value={metadata.water_withdrawal || ''}
                    onChange={(e) => handleInputChange('water_withdrawal', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="10,000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Water Discharge (KL)
                  </label>
                  <input
                    type="text"
                    value={metadata.water_discharge || ''}
                    onChange={(e) => handleInputChange('water_discharge', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="8,000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Water Recycled (%)
                  </label>
                  <input
                    type="text"
                    value={metadata.water_recycled_percent || ''}
                    onChange={(e) => handleInputChange('water_recycled_percent', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="30"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hazardous Waste (tonnes)
                  </label>
                  <input
                    type="text"
                    value={metadata.waste_hazardous || ''}
                    onChange={(e) => handleInputChange('waste_hazardous', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Non-Hazardous Waste (tonnes)
                  </label>
                  <input
                    type="text"
                    value={metadata.waste_non_hazardous || ''}
                    onChange={(e) => handleInputChange('waste_non_hazardous', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Waste Recycled (%)
                  </label>
                  <input
                    type="text"
                    value={metadata.waste_recycled_percent || ''}
                    onChange={(e) => handleInputChange('waste_recycled_percent', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="60"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Social & Employee Tab */}
          {activeTab === 'social' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Social & Employee Data</h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Total Employees
                  </label>
                  <input
                    type="text"
                    value={metadata.total_employees || ''}
                    onChange={(e) => handleInputChange('total_employees', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="1,000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gender Diversity (%)
                  </label>
                  <input
                    type="text"
                    value={metadata.gender_diversity_percent || ''}
                    onChange={(e) => handleInputChange('gender_diversity_percent', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="35"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Employee Turnover Rate (%)
                  </label>
                  <input
                    type="text"
                    value={metadata.employee_turnover_rate || ''}
                    onChange={(e) => handleInputChange('employee_turnover_rate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="12"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Average Training Hours
                  </label>
                  <input
                    type="text"
                    value={metadata.average_training_hours || ''}
                    onChange={(e) => handleInputChange('average_training_hours', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="40"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Safety Incidents
                  </label>
                  <input
                    type="text"
                    value={metadata.safety_incidents || ''}
                    onChange={(e) => handleInputChange('safety_incidents', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="5"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Governance Tab */}
          {activeTab === 'governance' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Governance Metrics</h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Board Independence (%)
                  </label>
                  <input
                    type="text"
                    value={metadata.board_independence_percent || ''}
                    onChange={(e) => handleInputChange('board_independence_percent', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Board Gender Diversity (%)
                  </label>
                  <input
                    type="text"
                    value={metadata.board_gender_diversity_percent || ''}
                    onChange={(e) => handleInputChange('board_gender_diversity_percent', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="30"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Anti-Corruption Training Coverage (%)
                  </label>
                  <input
                    type="text"
                    value={metadata.anti_corruption_training_percent || ''}
                    onChange={(e) => handleInputChange('anti_corruption_training_percent', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="100"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Supply Chain Tab */}
          {activeTab === 'supply_chain' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Supply Chain Metrics</h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Supplier ESG Coverage (%)
                  </label>
                  <input
                    type="text"
                    value={metadata.supplier_esg_coverage_percent || ''}
                    onChange={(e) => handleInputChange('supplier_esg_coverage_percent', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="75"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Local Supplier Spend (%)
                  </label>
                  <input
                    type="text"
                    value={metadata.local_supplier_spend_percent || ''}
                    onChange={(e) => handleInputChange('local_supplier_spend_percent', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="60"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Narratives Tab */}
          {activeTab === 'narratives' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Narrative Disclosures</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Materiality Assessment Summary
                  </label>
                  <textarea
                    value={metadata.materiality_assessment || ''}
                    onChange={(e) => handleInputChange('materiality_assessment', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    rows={4}
                    placeholder="Describe the materiality assessment process and key topics identified..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Board Oversight of ESG
                  </label>
                  <textarea
                    value={metadata.board_oversight || ''}
                    onChange={(e) => handleInputChange('board_oversight', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    rows={4}
                    placeholder="Describe how the board oversees ESG matters..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Stakeholder Engagement Approach
                  </label>
                  <textarea
                    value={metadata.stakeholder_engagement || ''}
                    onChange={(e) => handleInputChange('stakeholder_engagement', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    rows={4}
                    placeholder="Describe stakeholder engagement mechanisms and frequency..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Governance Structure Description
                  </label>
                  <textarea
                    value={metadata.governance_structure || ''}
                    onChange={(e) => handleInputChange('governance_structure', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    rows={4}
                    placeholder="Describe the governance structure for ESG management..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Climate-Related Disclosures
                  </label>
                  <textarea
                    value={metadata.climate_disclosures || ''}
                    onChange={(e) => handleInputChange('climate_disclosures', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    rows={4}
                    placeholder="Describe climate-related risks, opportunities, and strategies..."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Assurance Tab */}
          {activeTab === 'assurance' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">External Assurance</h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assurance Provider
                  </label>
                  <input
                    type="text"
                    value={metadata.assurance_provider || ''}
                    onChange={(e) => handleInputChange('assurance_provider', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="External audit firm name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assurance Scope
                  </label>
                  <input
                    type="text"
                    value={metadata.assurance_scope || ''}
                    onChange={(e) => handleInputChange('assurance_scope', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Limited/Reasonable assurance, covered topics"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportMetadata;
